/**
 * @fileoverview Practitioner-facing relation-back copy
 * @module keyDates/relationBackCopy
 *
 * Shared wording for an applied (non-statutory) relation-back date on a
 * VA / DOCA / SBR / provisional liquidation. Agents, activity callouts,
 * and the investigation UI all read from here so they cannot drift.
 */

/** Canonical callout when the relation-back date is applied, not statutory. */
export const APPLIED_RELATION_BACK_CALLOUT =
  "The relation-back date is assumed based on the applied date. Only a liquidator has the power to recover these transactions, so they can be pursued if the company goes into liquidation.";

/** True when a callout is already the current applied-RBD wording. */
export function isAppliedRelationBackCallout(text: string): boolean {
  return /assumed based on the applied date/i.test(text);
}

/**
 * True when a callout is the old "scenario only / caveated" wording the
 * agents used to emit from `relationBackAssumed`. Callouts are otherwise
 * free-form agent text (`flaggedHeadOutputSchema.callouts`), so the legacy
 * phrases alone are not a safe match — an unrelated callout could
 * coincidentally use one (e.g. "appointment date used to estimate
 * insolvency"). Require a relation-back marker too; legacy producers wrote
 * both "relation-back date" and "relation-back day".
 */
export function isLegacyAssumedRelationBackCallout(text: string): boolean {
  return (
    /\brelation-back\s+(?:date|day)\b/i.test(text) &&
    /caveated as a scenario|present as a scenario only|appointment date used|liquidation scenario can be analysed/i.test(
      text,
    )
  );
}

/**
 * Rewrites a stored activity-log callout that still uses the old assumed /
 * scenario language, so existing feeds update without a re-run.
 */
export function rewriteAssumedRelationBackCallout(text: string): string {
  if (
    isAppliedRelationBackCallout(text) ||
    isLegacyAssumedRelationBackCallout(text)
  ) {
    return APPLIED_RELATION_BACK_CALLOUT;
  }
  return text;
}

/**
 * Ensures the canonical applied-RBD callout is first when the date is
 * assumed, and drops any duplicate or legacy assumed-RBD notes the agent
 * also emitted.
 */
export function withAppliedRelationBackCallout(
  callouts: ReadonlyArray<string> | undefined,
  assumed: boolean,
): string[] {
  const rest = (callouts ?? []).filter(
    (note) =>
      !isLegacyAssumedRelationBackCallout(note) &&
      !isAppliedRelationBackCallout(note),
  );
  if (!assumed) return [...rest];
  return [APPLIED_RELATION_BACK_CALLOUT, ...rest];
}
